/* eslint-disable no-param-reassign */
import { createApi } from '@reduxjs/toolkit/query/react';
import isEqual from 'lodash.isequal';
import { networkBaseQuery } from './queries';
import { hiresV2Api } from './hiresV2';

const updateAllHiresV2Cache = ({ state, dispatch, updater }) => {
  const v2Cache = state?.hiresV2Api?.queries;
  const patchResults = [];

  if (v2Cache) {
    Object.values(v2Cache).forEach((queryData) => {
      if (queryData?.endpointName === 'getHiresV2' && queryData?.status === 'fulfilled') {
        const patchResult = dispatch(hiresV2Api.util.updateQueryData('getHiresV2', queryData.originalArgs, updater));
        patchResults.push(patchResult);
      }
    });
  }

  return patchResults;
};

export const hiresApi = createApi({
  reducerPath: 'hiresApi',
  baseQuery: networkBaseQuery,
  tagTypes: ['hires'],
  endpoints: (builder) => ({
    getHires: builder.query({
      query: ({ page = 1, perPage = 20 }) => ({
        url: '/contacts/hires',
        params: { page, perPage },
      }),
      serializeQueryArgs: ({ endpointName }) => endpointName,
      forceRefetch: ({ currentArg, previousArg }) => !isEqual(currentArg, previousArg),
      merge: (currentCache, incomingState) => {
        currentCache.items.push(...incomingState.items);
      },
    }),
    updateHire: builder.mutation({
      query: ({ hire, data }) => ({ url: `/contacts/hires/${hire.id}`, method: 'patch', data }),
      async onQueryStarted({ hire, data }, { dispatch, queryFulfilled, getState }) {
        // Update v1 cache
        const patchResult = dispatch(
          hiresApi.util.updateQueryData('getHires', undefined, ({ items }) => {
            const currentHire = items.find((item) => item.id === hire.id);

            if (currentHire) {
              currentHire.teamInvolvement = data.teamInvolvement;
            }
          }),
        );

        // Update v2 cache - update all cache entries
        const patchResultsV2 = updateAllHiresV2Cache({
          state: getState(),
          dispatch,
          updater: (draft) => {
            if (draft?.items && Array.isArray(draft.items)) {
              const currentHire = draft.items.find((item) => item.id === hire.id);
              if (currentHire) {
                const wasPendingReview =
                  currentHire.teamInvolvement === 'pending_review' || currentHire.team_involvement === 'pending_review';
                const isNowPendingReview = data.teamInvolvement === 'pending_review';

                // Update the pending review count in meta, supporting both legacy and query-based fields
                if (draft.meta) {
                  const currentPending =
                    typeof draft.meta.queryPendingReview === 'number'
                      ? draft.meta.queryPendingReview
                      : draft.meta.pendingReview;

                  if (typeof currentPending === 'number') {
                    let nextPending = currentPending;

                    if (wasPendingReview && !isNowPendingReview) {
                      nextPending = Math.max(0, currentPending - 1);
                    } else if (!wasPendingReview && isNowPendingReview) {
                      nextPending += 1;
                    }

                    if (draft.meta.queryPendingReview !== undefined) {
                      draft.meta.queryPendingReview = nextPending;
                    }
                    if (draft.meta.pendingReview !== undefined) {
                      draft.meta.pendingReview = nextPending;
                    }
                  }
                }

                currentHire.teamInvolvement = data.teamInvolvement;
                // Also update team_involvement for consistency
                currentHire.team_involvement = data.teamInvolvement;
              }
            }
          },
        });

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
          patchResultsV2.forEach((p) => p.undo());
        }
      },
    }),
    deleteHire: builder.mutation({
      query: (hire) => ({ url: `/contacts/hires/${hire.id}`, method: 'delete' }),
      async onQueryStarted(hire, { dispatch, queryFulfilled, getState }) {
        // Update v1 cache
        const patchResult = dispatch(
          hiresApi.util.updateQueryData('getHires', undefined, ({ items }) => {
            const index = items.findIndex((item) => item.id === hire.id);

            if (index !== -1) {
              items.splice(index, 1);
            }
          }),
        );

        // Update v2 cache - update all cache entries
        const patchResultsV2Delete = updateAllHiresV2Cache({
          state: getState(),
          dispatch,
          updater: (draft) => {
            if (draft?.items && Array.isArray(draft.items)) {
              const index = draft.items.findIndex((item) => item.id === hire.id);
              if (index !== -1) {
                const deletedHire = draft.items[index];
                const isPendingTeamInvolvement = deletedHire.teamInvolvement === 'pending_review';
                const isPendingTeamInvolvementAlt = deletedHire.team_involvement === 'pending_review';
                const wasPendingReview = isPendingTeamInvolvement || isPendingTeamInvolvementAlt;

                // Decrease pending review count if the deleted hire was pending review
                if (wasPendingReview && draft.meta) {
                  const currentPending =
                    typeof draft.meta.queryPendingReview === 'number'
                      ? draft.meta.queryPendingReview
                      : draft.meta.pendingReview;

                  if (typeof currentPending === 'number') {
                    const nextPending = Math.max(0, currentPending - 1);

                    if (draft.meta.queryPendingReview !== undefined) {
                      draft.meta.queryPendingReview = nextPending;
                    }
                    if (draft.meta.pendingReview !== undefined) {
                      draft.meta.pendingReview = nextPending;
                    }
                  }
                }

                draft.items.splice(index, 1);
              }
            }
          },
        });

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
          patchResultsV2Delete.forEach((p) => p.undo());
        }
      },
    }),
    downloadHires: builder.mutation({
      query: () => ({ url: '/contacts/hires/export', method: 'post' }),
    }),
  }),
});

export const { useGetHiresQuery, useDeleteHireMutation, useUpdateHireMutation, useDownloadHiresMutation } = hiresApi;
